import React, { useId, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { useTheme } from '../../../context/ThemeContext';
import {
    DAILY_TIMELINE_PRESETS,
    DEFAULT_DAILY_TIMELINE_DAYS,
    DEFAULT_WEEKLY_TIMELINE_WEEKS,
    WEEKLY_TIMELINE_PRESETS,
} from '../../../utils/chartTimeline';
import ThemedSelect from '../../../components/ui/ThemedSelect';

/* ------------------------------------------------------------------ */
/* Layout constants (all chart math is in this single coordinate space) */
/* ------------------------------------------------------------------ */

const W = 820;
const H = 200;
const PAD_L = 50;
const PAD_R = 24;
const PAD_T = 16;
const PAD_B = 30;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const niceMax = (val) => {
    if (val <= 0) return 100;
    const exp = Math.pow(10, Math.floor(Math.log10(val)));
    const f = val / exp;
    let nf;
    if (f <= 1) nf = 1;
    else if (f <= 2) nf = 2;
    else if (f <= 5) nf = 5;
    else nf = 10;
    return nf * exp;
};

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatCurrencyTick = (v, maxVal) => {
    if (maxVal <= 0.001 && v > 0) {
        return `$${v.toLocaleString(undefined, {
            maximumFractionDigits: 8,
            minimumFractionDigits: 0,
        })}`;
    }
    if (v >= 1000) {
        const k = v / 1000;
        return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
    }
    if (maxVal <= 10 && !Number.isInteger(v)) {
        return `$${v.toLocaleString(undefined, {
            maximumFractionDigits: maxVal < 1 ? 6 : 4,
            minimumFractionDigits: 0,
        })}`;
    }
    return `$${Math.round(v)}`;
};

const clampPlotY = (y, baseY) => Math.min(Math.max(y, PAD_T), baseY);

const bezierPoint = (t, p0, p1, p2, p3) => {
    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    return {
        x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
        y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
    };
};

/** Straight segments — fallback for very short series. */
const linearPath = (pts, baseY) => {
    if (!pts || pts.length === 0) return '';
    return pts
        .map((p, i) => {
            const y = clampPlotY(p.y, baseY);
            return `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${y.toFixed(2)}`;
        })
        .join(' ');
};

/**
 * Smooth cardinal-style curve with Y clamped to the zero baseline.
 * Samples each segment so spline overshoot never dips below $0 on sparse data.
 */
const smoothClampedPath = (pts, baseY, samplesPerSegment = 14) => {
    if (!pts || pts.length === 0) return '';
    if (pts.length <= 2) return linearPath(pts, baseY);

    const segments = [];
    let pathStarted = false;

    for (let i = 0; i < pts.length - 1; i += 1) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];

        const start = { x: p1.x, y: clampPlotY(p1.y, baseY) };
        const end = { x: p2.x, y: clampPlotY(p2.y, baseY) };
        const cp1 = {
            x: p1.x + (p2.x - p0.x) / 6,
            y: clampPlotY(p1.y + (p2.y - p0.y) / 6, baseY),
        };
        const cp2 = {
            x: p2.x - (p3.x - p1.x) / 6,
            y: clampPlotY(p2.y - (p3.y - p1.y) / 6, baseY),
        };

        for (let s = 0; s <= samplesPerSegment; s += 1) {
            if (i > 0 && s === 0) continue;
            const t = s / samplesPerSegment;
            const pt = bezierPoint(t, start, cp1, cp2, end);
            pt.y = clampPlotY(pt.y, baseY);
            segments.push(
                `${pathStarted ? 'L' : 'M'} ${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`,
            );
            pathStarted = true;
        }
    }

    return segments.join(' ');
};

const smoothClampedArea = (pts, baseY) => {
    if (!pts.length) return '';
    const top = smoothClampedPath(pts, baseY);
    return `${top} L ${pts[pts.length - 1].x.toFixed(2)} ${baseY} L ${pts[0].x.toFixed(2)} ${baseY} Z`;
};

/** Grouped bar with flat bottom and rounded top (Sent vs Received look). */
const compareBarPath = (xLeft, bottomY, w, h, rMax = 6) => {
    if (!w || h <= 0) return '';
    if (h < 0.5) {
        return `M ${xLeft},${bottomY} L ${xLeft + w},${bottomY} Z`;
    }
    const top = bottomY - h;
    const r = Math.min(rMax, w / 2, h);
    if (r <= 0.25) {
        return `M ${xLeft},${bottomY} L ${xLeft},${top} L ${xLeft + w},${top} L ${xLeft + w},${bottomY} Z`;
    }
    return [
        `M ${xLeft},${bottomY}`,
        `L ${xLeft},${top + r}`,
        `Q ${xLeft},${top} ${xLeft + r},${top}`,
        `L ${xLeft + w - r},${top}`,
        `Q ${xLeft + w},${top} ${xLeft + w},${top + r}`,
        `L ${xLeft + w},${bottomY}`,
        'Z',
    ].join(' ');
};

/* ------------------------------------------------------------------ */
/* Tooltip                                                            */
/* ------------------------------------------------------------------ */

const formatTooltipValue = (n, valueFormat, chartMax = 1) => {
    if (valueFormat === 'number') {
        return (n ?? 0).toLocaleString(undefined, {
            maximumFractionDigits: chartMax <= 0.001 ? 8 : chartMax < 1 ? 6 : 0,
        });
    }
    const decimals =
        chartMax <= 0.0001 ? 8 : chartMax <= 0.001 ? 6 : chartMax < 1 ? 4 : 2;
    return `$${(n ?? 0).toLocaleString(undefined, {
        maximumFractionDigits: decimals,
        minimumFractionDigits: 0,
    })}`;
};

const Tooltip = ({
    x,
    y,
    day,
    isDark,
    primaryLabel,
    secondaryLabel,
    primaryValueFormat,
    secondaryValueFormat,
    showPrimary = true,
    chartMax = 1,
}) => {
    if (!day) return null;
    // Keep the tooltip inside the plot area horizontally
    const halfW = 110;
    const tx = Math.min(W - halfW, Math.max(halfW, x));
    const boxClass = isDark
        ? 'rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-[11px] text-zinc-200 shadow-lg'
        : 'rounded-lg border border-zinc-200 bg-white px-3 py-2 text-[11px] text-zinc-800 shadow-lg';
    const muted = isDark ? 'text-zinc-500' : 'text-zinc-500';
    const sentAmt = isDark ? 'text-zinc-200' : 'text-zinc-700';
    const recvAmt = isDark ? 'text-teal-300' : 'text-teal-800';
    return (
        <foreignObject
            x={tx - halfW}
            y={Math.max(0, y - 78)}
            width={halfW * 2}
            height={68}
            style={{ pointerEvents: 'none' }}
        >
            <div xmlns="http://www.w3.org/1999/xhtml" className={boxClass}>
                <p className={`mb-1 text-[10px] uppercase tracking-wider ${muted}`}>
                    {formatDate(day.labelDate || day.date)}
                </p>
                {showPrimary ? (
                    <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-1.5">
                            <span className="h-2 w-2 shrink-0 rounded-full bg-zinc-300 dark:bg-zinc-200" />
                            {primaryLabel}
                        </span>
                        <span className={`font-semibold tabular-nums ${sentAmt}`}>
                            {formatTooltipValue(day.sent, primaryValueFormat, chartMax)}
                        </span>
                    </div>
                ) : null}
                <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-teal-400" />
                        {secondaryLabel}
                    </span>
                    <span className={`font-semibold tabular-nums ${recvAmt}`}>
                        {formatTooltipValue(day.received, secondaryValueFormat, chartMax)}
                    </span>
                </div>
            </div>
        </foreignObject>
    );
};

/* ------------------------------------------------------------------ */
/* Main chart                                                         */
/* ------------------------------------------------------------------ */

const TransactionVolumeChart = ({
    data = [],
    loading = false,
    title = 'Transaction Volume',
    subtitle = 'Activity over time',
    primaryLabel = 'Sent',
    secondaryLabel = 'Received',
    trendTabLabel = 'Trend',
    compareTabLabel = 'Compare',
    yAxisTickFormat,
    /** 'currency' (default) or 'number' for tooltip values */
    valueFormat = 'currency',
    primaryValueFormat,
    secondaryValueFormat,
    /** When false, chart shows only the received series (earnings / payouts). */
    showSentSeries = true,
    /** When false, hides compare tab (e.g. stats page earnings-only view). */
    enableCompare = true,
    initialMode = 'trend',
    /** Show timeline dropdown (1 month / 3 months / etc.). */
    enableTimeline = false,
    /** 'weeks' for jobs portal weekly charts, 'days' for daily payment charts. */
    timelineMode = 'weeks',
    timelineWeeks = DEFAULT_WEEKLY_TIMELINE_WEEKS,
    timelineDays = DEFAULT_DAILY_TIMELINE_DAYS,
    onTimelineChange,
}) => {
    const { resolvedTheme } = useTheme();
    const isDark = resolvedTheme === 'dark';
    const tooltipPrimaryFormat = primaryValueFormat ?? valueFormat;
    const tooltipSecondaryFormat = secondaryValueFormat ?? valueFormat;
    const [mode, setMode] = useState(() =>
        !enableCompare ? 'trend' : initialMode === 'compare' ? 'compare' : 'trend',
    );
    const [hoverIdx, setHoverIdx] = useState(null);

    const timelinePresets =
        timelineMode === 'days' ? DAILY_TIMELINE_PRESETS : WEEKLY_TIMELINE_PRESETS;
    const timelineValue = timelineMode === 'days' ? timelineDays : timelineWeeks;

    const handleTimelineSelect = (event) => {
        const next = Number(event.target.value);
        if (!Number.isFinite(next)) return;
        onTimelineChange?.(next, timelineMode);
    };

    const chartPalette = useMemo(() => {
        if (isDark) {
            return {
                grid: 'rgba(161,161,170,0.12)',
                axis: 'rgba(161,161,170,0.55)',
                sentFill0: 'rgb(161, 161, 170)',
                recvFill0: 'rgb(45, 212, 191)',
                sentStroke0: 'rgb(212, 212, 216)',
                sentStroke1: 'rgb(161, 161, 170)',
                recvStroke0: 'rgb(45, 212, 191)',
                recvStroke1: 'rgb(20, 184, 166)',
                markerFill: 'rgb(24,24,27)',
                sentRing: 'rgb(161, 161, 170)',
                recvRing: 'rgb(45, 212, 191)',
                hoverLine: 'rgba(161,161,170,0.25)',
                plotBg: 'rgb(9,9,11)',
                compareSent: 'rgb(161, 161, 170)',
                compareSentDeep: 'rgb(113, 113, 122)',
                compareRecv: 'rgb(45, 212, 191)',
                compareRecvDeep: 'rgb(20, 184, 166)',
            };
        }
        return {
            grid: 'rgba(24,24,27,0.08)',
            axis: 'rgba(82,82,91,0.75)',
            sentFill0: 'rgb(100, 116, 139)',
            recvFill0: 'rgb(20, 184, 166)',
            sentStroke0: 'rgb(100, 116, 139)',
            sentStroke1: 'rgb(71, 85, 105)',
            recvStroke0: 'rgb(13, 148, 136)',
            recvStroke1: 'rgb(15, 118, 110)',
            markerFill: 'rgb(255,255,255)',
            sentRing: 'rgb(100, 116, 139)',
            recvRing: 'rgb(13, 148, 136)',
            hoverLine: 'rgba(24,24,27,0.12)',
            plotBg: 'rgb(250,250,250)',
            compareSent: 'rgb(148, 163, 184)',
            compareSentDeep: 'rgb(100, 116, 139)',
            compareRecv: 'rgb(20, 184, 166)',
            compareRecvDeep: 'rgb(13, 148, 136)',
        };
    }, [isDark]);

    const gradId = useId().replace(/:/g, '');

    const safeData = data && data.length > 0
        ? data
        : Array.from({ length: timelineMode === 'days' ? timelineDays : timelineWeeks }, (_, i) => {
              const d = new Date();
              d.setHours(0, 0, 0, 0);
              const span = timelineMode === 'days' ? timelineDays : timelineWeeks;
              d.setDate(d.getDate() - (span - 1 - i));
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              return { date: key, labelDate: key, sent: 0, received: 0 };
          });

    const { sentPts, receivedPts, max, ticks, tickLabels, dateLabels, compareDateLabels } = useMemo(() => {
        const peak = Math.max(
            ...safeData.map((d) =>
                showSentSeries
                    ? Math.max(d.sent || 0, d.received || 0)
                    : d.received || 0,
            ),
            0,
        );
        const maxRaw = peak > 0 ? peak : 1;
        const max = niceMax(maxRaw);
        const ticks = Array.from({ length: 5 }, (_, i) => (max * (4 - i)) / 4);
        const tickLabels = ticks.map((t) =>
            yAxisTickFormat ? yAxisTickFormat(t) : formatCurrencyTick(t, max),
        );
        const denom = Math.max(1, safeData.length - 1);
        const sentPts = safeData.map((d, i) => ({
            x: PAD_L + (i / denom) * PLOT_W,
            y: PAD_T + (1 - (d.sent || 0) / max) * PLOT_H,
        }));
        const receivedPts = safeData.map((d, i) => ({
            x: PAD_L + (i / denom) * PLOT_W,
            y: PAD_T + (1 - (d.received || 0) / max) * PLOT_H,
        }));
        const indices = [
            0,
            Math.floor(safeData.length / 4),
            Math.floor(safeData.length / 2),
            Math.floor((safeData.length * 3) / 4),
            safeData.length - 1,
        ];
        const dateLabels = indices
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .map((i) => ({
                x: PAD_L + (i / denom) * PLOT_W,
                label: safeData[i]
                    ? formatDate(safeData[i].labelDate || safeData[i].date)
                    : '',
            }));
        const n = safeData.length;
        const bucketW = n > 0 ? PLOT_W / n : PLOT_W;
        const compareDateLabels = indices
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .map((i) => ({
                x: n > 0 ? PAD_L + (i + 0.5) * bucketW : PAD_L,
                label: safeData[i]
                    ? formatDate(safeData[i].labelDate || safeData[i].date)
                    : '',
            }));
        return { sentPts, receivedPts, max, ticks, tickLabels, dateLabels, compareDateLabels };
    }, [safeData, showSentSeries, yAxisTickFormat]);

    const baseY = PAD_T + PLOT_H;
    const sentPath = smoothClampedPath(sentPts, baseY);
    const receivedPath = smoothClampedPath(receivedPts, baseY);
    const sentArea = sentPts.length ? smoothClampedArea(sentPts, baseY) : '';
    const receivedArea = receivedPts.length ? smoothClampedArea(receivedPts, baseY) : '';

    /* ----- Pointer handling for hover scrubbing ----- */
    const handlePointerMove = (e) => {
        const svg = e.currentTarget;
        const pt = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const ctm = svg.getScreenCTM();
        if (!ctm) return;
        const local = pt.matrixTransform(ctm.inverse());
        const n = safeData.length;
        if (n <= 0) {
            setHoverIdx(null);
            return;
        }
        if (mode === 'compare') {
            const rel = local.x - PAD_L;
            if (rel < 0 || rel > PLOT_W) {
                setHoverIdx(null);
                return;
            }
            const bucketW = PLOT_W / n;
            const idx = Math.min(n - 1, Math.max(0, Math.floor(rel / bucketW)));
            setHoverIdx(idx);
        } else {
            const ratio = Math.min(1, Math.max(0, (local.x - PAD_L) / PLOT_W));
            const denom = Math.max(1, n - 1);
            const idx = Math.round(ratio * denom);
            setHoverIdx(idx);
        }
    };

    const sentFillUrl = `url(#sentFill-${gradId})`;
    const receivedFillUrl = `url(#receivedFill-${gradId})`;
    const sentStrokeUrl = `url(#sentStroke-${gradId})`;
    const receivedStrokeUrl = `url(#receivedStroke-${gradId})`;

    const nDays = safeData.length;
    const hoverAnchorX =
        hoverIdx !== null && nDays > 0 && hoverIdx < nDays && safeData[hoverIdx]
            ? mode === 'compare'
                ? PAD_L + (hoverIdx + 0.5) * (PLOT_W / nDays)
                : sentPts[hoverIdx]?.x ?? null
            : null;

    const xAxisLabels = mode === 'compare' ? compareDateLabels : dateLabels;

    return (
        <div className="relative rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 dark:shadow-none sm:p-6 md:rounded-2xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                        {title}
                    </h3>
                    <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-500">
                        {subtitle}
                    </p>
                </div>

                {enableTimeline || enableCompare ? (
                    <div className="inline-flex flex-wrap items-stretch self-start rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900/80">
                        {enableCompare ? (
                            <div className="flex items-center gap-1.5 px-2 py-1">
                                <span className="hidden pl-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 sm:inline">
                                    View
                                </span>
                                <div className="inline-flex items-center gap-0.5 p-0.5">
                                    {[
                                        { id: 'trend', label: trendTabLabel },
                                        { id: 'compare', label: compareTabLabel },
                                    ].map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => {
                                                setMode(t.id);
                                                setHoverIdx(null);
                                            }}
                                            className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-colors sm:px-3 ${
                                                mode === t.id
                                                    ? 'bg-teal-700 text-white shadow-sm dark:bg-teal-600'
                                                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                        {enableCompare && enableTimeline ? (
                            <div
                                className="hidden w-px self-stretch bg-zinc-200 dark:bg-zinc-700 sm:block"
                                aria-hidden
                            />
                        ) : null}
                        {enableTimeline ? (
                            <div className="inline-flex min-h-[36px] items-center gap-2 border-t border-zinc-200 px-2.5 py-1.5 dark:border-zinc-700 sm:border-t-0">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                                    Range
                                </span>
                                <ThemedSelect
                                    size="compact"
                                    value={timelineValue}
                                    onChange={handleTimelineSelect}
                                    options={timelinePresets.map((preset) => ({
                                        value:
                                            timelineMode === 'days'
                                                ? preset.days
                                                : preset.weeks,
                                        label: preset.label,
                                    }))}
                                />
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {showSentSeries ? (
                <div className="mt-2 flex flex-wrap items-center gap-4">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-400">
                        <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: chartPalette.sentStroke0 }}
                        />
                        {primaryLabel}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-400">
                        <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: chartPalette.recvStroke0 }}
                        />
                        {secondaryLabel}
                    </span>
                </div>
            ) : null}

            <div
                className="relative mt-3 w-full max-w-full"
                style={{ aspectRatio: `${W} / ${H}` }}
            >
                {loading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-zinc-950/60">
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">Loading chart…</span>
                    </div>
                )}
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="xMidYMid meet"
                    className="block h-full w-full cursor-crosshair"
                    onPointerMove={handlePointerMove}
                    onPointerLeave={() => setHoverIdx(null)}
                >
                    <defs>
                        <linearGradient id={`sentFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={chartPalette.sentFill0} stopOpacity="0.28" />
                            <stop offset="100%" stopColor={chartPalette.sentFill0} stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id={`receivedFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={chartPalette.recvFill0} stopOpacity="0.28" />
                            <stop offset="100%" stopColor={chartPalette.recvFill0} stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id={`sentStroke-${gradId}`} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={chartPalette.sentStroke0} />
                            <stop offset="100%" stopColor={chartPalette.sentStroke1} />
                        </linearGradient>
                        <linearGradient id={`receivedStroke-${gradId}`} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={chartPalette.recvStroke0} />
                            <stop offset="100%" stopColor={chartPalette.recvStroke1} />
                        </linearGradient>
                    </defs>

                    <rect
                        x={PAD_L}
                        y={PAD_T}
                        width={PLOT_W}
                        height={PLOT_H}
                        rx={8}
                        fill={chartPalette.plotBg}
                        opacity={isDark ? 1 : 0.85}
                    />

                    {ticks.map((t, i) => {
                        const y = PAD_T + (i / (ticks.length - 1)) * PLOT_H;
                        return (
                            <g key={`tick-${i}`}>
                                <line
                                    x1={PAD_L}
                                    x2={W - PAD_R}
                                    y1={y}
                                    y2={y}
                                    stroke={chartPalette.grid}
                                    strokeDasharray="3 4"
                                />
                                <text
                                    x={PAD_L - 8}
                                    y={y + 3}
                                    textAnchor="end"
                                    fontSize="10"
                                    fill={chartPalette.axis}
                                    fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                                >
                                    {tickLabels[i]}
                                </text>
                            </g>
                        );
                    })}

                    {xAxisLabels.map((d, i) => (
                        <text
                            key={`x-${i}-${mode}`}
                            x={d.x}
                            y={H - 12}
                            textAnchor="middle"
                            fontSize="10"
                            fill={chartPalette.axis}
                            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                        >
                            {d.label}
                        </text>
                    ))}

                    {mode === 'trend' ? (
                        <g key="trend">
                            {showSentSeries ? (
                                <motion.path
                                    d={sentArea}
                                    fill={sentFillUrl}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.8, delay: 0.3 }}
                                />
                            ) : null}
                            <motion.path
                                d={receivedArea}
                                fill={receivedFillUrl}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                            />

                            {showSentSeries ? (
                                <motion.path
                                    d={sentPath}
                                    fill="none"
                                    stroke={sentStrokeUrl}
                                    strokeWidth={2.2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    initial={{ pathLength: 0, opacity: 0.4 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                                />
                            ) : null}
                            <motion.path
                                d={receivedPath}
                                fill="none"
                                stroke={receivedStrokeUrl}
                                strokeWidth={2.2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                initial={{ pathLength: 0, opacity: 0.4 }}
                                animate={{ pathLength: 1, opacity: 1 }}
                                transition={{ duration: 1.4, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                            />

                            {hoverIdx !== null && receivedPts[hoverIdx] && hoverAnchorX !== null && (
                                <g>
                                    <line
                                        x1={hoverAnchorX}
                                        x2={hoverAnchorX}
                                        y1={PAD_T}
                                        y2={baseY}
                                        stroke={chartPalette.hoverLine}
                                        strokeDasharray="4 4"
                                    />
                                    {showSentSeries ? (
                                        <circle
                                            cx={sentPts[hoverIdx].x}
                                            cy={sentPts[hoverIdx].y}
                                            r="5"
                                            fill={chartPalette.markerFill}
                                            stroke={chartPalette.sentRing}
                                            strokeWidth="2"
                                        />
                                    ) : null}
                                    <circle
                                        cx={receivedPts[hoverIdx].x}
                                        cy={receivedPts[hoverIdx].y}
                                        r="5"
                                        fill={chartPalette.markerFill}
                                        stroke={chartPalette.recvRing}
                                        strokeWidth="2"
                                    />
                                </g>
                            )}
                        </g>
                    ) : (
                        <g key="compare">
                            {safeData.map((d, i) => {
                                const n = safeData.length;
                                const bucketW = n > 0 ? PLOT_W / n : PLOT_W;
                                const cx = PAD_L + (i + 0.5) * bucketW;
                                const pairGap = 3;
                                const maxHalf = Math.max(2.5, (bucketW - pairGap) / 2 - 1);
                                const barW = Math.min(16, maxHalf);
                                const sentVal = d.sent || 0;
                                const recvVal = d.received || 0;
                                let sentH = (sentVal / max) * PLOT_H;
                                let recvH = (recvVal / max) * PLOT_H;
                                if (sentVal > 0 && sentH > 0 && sentH < 2) sentH = 2;
                                if (recvVal > 0 && recvH > 0 && recvH < 2) recvH = 2;
                                const xSent = cx - barW - pairGap / 2;
                                const xRecv = cx + pairGap / 2;
                                const sentPathD = compareBarPath(xSent, baseY, barW, sentH, 7);
                                const recvPathD = compareBarPath(xRecv, baseY, barW, recvH, 7);
                                return (
                                    <g key={`bar-${i}`}>
                                        <motion.path
                                            d={sentPathD}
                                            fill={chartPalette.compareSent}
                                            stroke={chartPalette.compareSentDeep}
                                            strokeWidth={1}
                                            strokeOpacity={0.45}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: hoverIdx === null || hoverIdx === i ? 1 : 0.45 }}
                                            transition={{ duration: 0.35, delay: i * 0.02 }}
                                        />
                                        <motion.path
                                            d={recvPathD}
                                            fill={chartPalette.compareRecv}
                                            stroke={chartPalette.compareRecvDeep}
                                            strokeWidth={1}
                                            strokeOpacity={0.45}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: hoverIdx === null || hoverIdx === i ? 1 : 0.45 }}
                                            transition={{ duration: 0.35, delay: 0.04 + i * 0.02 }}
                                        />
                                    </g>
                                );
                            })}
                            {hoverIdx !== null && safeData[hoverIdx] && hoverAnchorX !== null && (
                                <line
                                    x1={hoverAnchorX}
                                    x2={hoverAnchorX}
                                    y1={PAD_T}
                                    y2={baseY}
                                    stroke={chartPalette.hoverLine}
                                    strokeDasharray="4 4"
                                />
                            )}
                        </g>
                    )}

                    {hoverIdx !== null && safeData[hoverIdx] && hoverAnchorX !== null && (
                        <Tooltip
                            x={hoverAnchorX}
                            y={Math.min(
                                showSentSeries ? sentPts[hoverIdx]?.y ?? baseY : baseY,
                                receivedPts[hoverIdx]?.y ?? baseY,
                            )}
                            day={safeData[hoverIdx]}
                            isDark={isDark}
                            primaryLabel={primaryLabel}
                            secondaryLabel={secondaryLabel}
                            primaryValueFormat={tooltipPrimaryFormat}
                            secondaryValueFormat={tooltipSecondaryFormat}
                            showPrimary={showSentSeries}
                            chartMax={max}
                        />
                    )}
                </svg>
            </div>
        </div>
    );
};

export default TransactionVolumeChart;
